import React, { useRef, useCallback, useMemo, useState, useLayoutEffect, useEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { LogsViewerProps } from "../types";
import { LogRow } from "./LogRow";
import { extractCommonLabels } from "../utils/labelProcessor";
import { Spin } from "antd";
import "./LogsViewer.css";

const LABELS_WIDTH = 300;
const ESTIMATED_ROW_HEIGHT = 33;
const OVERSCAN = 10;
const SCROLL_THRESHOLD = 50;

/**
 * Find the nearest scrollable ancestor element
 */
function findScrollParent(element: HTMLElement | null): HTMLElement | null {
  if (!element) return null;

  let parent = element.parentElement;
  while (parent) {
    const style = getComputedStyle(parent);
    const overflowY = style.overflowY;
    if (overflowY === "auto" || overflowY === "scroll") {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

/**
 * Virtualized log viewer using TanStack Virtual
 * Uses the page's scroll container for virtualization (not internal scroll)
 */
export function LogsViewer({
  logs,
  isLoading = false,
  isLive = false,
  liveStatus,
  settings,
  commonLabels: propCommonLabels,
  width,
  error,
  hasExecuted = false,
  hasUniqueLabels = true,
  bufferedCount = 0,
  onPauseChange,
  onResume,
  onLabelClick,
}: LogsViewerProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  const isAtEdgeRef = useRef(true);
  const isResumingRef = useRef(false);

  // Find the scrollable parent and calculate offset
  useLayoutEffect(() => {
    if (listRef.current) {
      const parent = findScrollParent(listRef.current);
      setScrollParent(parent);

      // Calculate offset from scroll parent
      if (parent && listRef.current) {
        const parentRect = parent.getBoundingClientRect();
        const listRect = listRef.current.getBoundingClientRect();
        setScrollMargin(listRect.top - parentRect.top + parent.scrollTop);
      }
    }
  }, [logs.length]);

  // Use provided common labels or calculate them
  const commonLabels = useMemo(() => {
    return propCommonLabels ?? extractCommonLabels(logs);
  }, [propCommonLabels, logs]);

  // Compute effective settings
  // - Hide labels column if no unique labels exist
  // - Force wrap when prettifyJSON is enabled
  const effectiveShowLabels = settings.showLabels && hasUniqueLabels;
  const effectiveWrapLines = settings.wrapLines || settings.prettifyJSON;

  // Virtualizer using the page's scroll container
  // getItemKey ensures measurements stay associated with correct log entries
  // when logs are prepended in live mode (indices shift but UUIDs are stable)
  const virtualizer = useVirtualizer({
    count: logs.length,
    getScrollElement: () => scrollParent,
    estimateSize: useCallback(() => ESTIMATED_ROW_HEIGHT, []),
    overscan: OVERSCAN,
    getItemKey: useCallback((index: number) => logs[index]?.id ?? String(index), [logs]),
    scrollMargin,
  });

  const virtualItems = virtualizer.getVirtualItems();

  // Scroll detection for live pause
  useEffect(() => {
    if (!isLive || liveStatus !== "connected" || !scrollParent || !onPauseChange) {
      // Reset edge state when not in live mode
      isAtEdgeRef.current = true;
      return;
    }

    const handleScroll = () => {
      let atEdge: boolean;

      if (settings.orderBy === "desc") {
        // Newest at top — "at edge" means scrolled near top
        atEdge = scrollParent.scrollTop <= scrollMargin + SCROLL_THRESHOLD;
      } else {
        // Newest at bottom — "at edge" means scrolled near bottom
        const scrollBottom = scrollParent.scrollTop + scrollParent.clientHeight;
        atEdge = scrollParent.scrollHeight - scrollBottom <= SCROLL_THRESHOLD;
      }

      // While a programmatic resume-scroll is animating, don't re-trigger pause.
      // Clear the guard once the scroll reaches the edge.
      if (isResumingRef.current) {
        if (atEdge) {
          isResumingRef.current = false;
        }
        isAtEdgeRef.current = atEdge;
        return;
      }

      // Only trigger pause on transition from "at edge" to "away from edge"
      if (isAtEdgeRef.current && !atEdge) {
        onPauseChange(true);
      }

      isAtEdgeRef.current = atEdge;
    };

    scrollParent.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      scrollParent.removeEventListener("scroll", handleScroll);
    };
  }, [isLive, liveStatus, scrollParent, scrollMargin, settings.orderBy, onPauseChange]);

  // Resume handler — flush buffer and scroll to the new-logs edge
  const handleResume = useCallback(() => {
    if (!onResume) return;

    onResume();
    isAtEdgeRef.current = true;
    isResumingRef.current = true;

    if (scrollParent) {
      if (settings.orderBy === "desc") {
        scrollParent.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        requestAnimationFrame(() => {
          scrollParent.scrollTo({
            top: scrollParent.scrollHeight,
            behavior: "smooth",
          });
        });
      }
    }

    // Safety timeout: clear guard if scroll never reaches edge
    setTimeout(() => {
      isResumingRef.current = false;
    }, 1000);
  }, [onResume, scrollParent, settings.orderBy]);

  // Show error state
  if (error) {
    return (
      <div className="logs-viewer logs-viewer--error" style={{ width, minHeight: 200 }}>
        <div className="logs-viewer-message logs-viewer-message--error">
          {error}
        </div>
      </div>
    );
  }

  // Show loading state
  if (isLoading && logs.length === 0) {
    return (
      <div className="logs-viewer logs-viewer--loading" style={{ width, minHeight: 200 }}>
        <Spin />
      </div>
    );
  }

  // Show empty state - different messages based on execution state
  if (!isLoading && logs.length === 0) {
    return (
      <div className="logs-viewer logs-viewer--empty" style={{ width, minHeight: 200 }}>
        <div className="logs-viewer-message">
          {hasExecuted ? "No logs found" : "Run a query or start live mode"}
        </div>
      </div>
    );
  }

  return (
    <div className={`logs-viewer ${effectiveWrapLines ? '' : 'logs-viewer--nowrap'}`} style={{ width }} ref={listRef}>
      {/* Live status indicator */}
      {isLive && (
        <div className="logs-viewer-live-indicator">
          <span
            className={`logs-viewer-live-dot logs-viewer-live-dot--${liveStatus}`}
          />
          <span className="logs-viewer-live-text">
            {liveStatus === "connected"
              ? "Live"
              : liveStatus === "connecting"
                ? "Connecting..."
                : liveStatus === "error"
                  ? "Error"
                  : "Disconnected"}
          </span>
        </div>
      )}

      {/* Buffered logs banner — shown when live is paused by scrolling */}
      {isLive && bufferedCount > 0 && (
        <div
          className={`logs-viewer-buffered-banner logs-viewer-buffered-banner--${settings.orderBy === "desc" ? "top" : "bottom"}`}
          onClick={handleResume}
        >
          <span className="logs-viewer-buffered-banner-text">
            {bufferedCount} new log{bufferedCount !== 1 ? "s" : ""}
          </span>
          <span className="logs-viewer-buffered-banner-arrow">
            {settings.orderBy === "desc" ? "\u2191" : "\u2193"}
          </span>
        </div>
      )}

      {/* Virtualized content - scrolls with window */}
      <div
        className="logs-viewer-content"
        style={{
          height: virtualizer.getTotalSize(),
          position: "relative",
        }}
      >
        {virtualItems.map((virtualRow) => {
          const entry = logs[virtualRow.index];
          return (
            <div
              key={entry.id}
              ref={virtualizer.measureElement}
              data-index={virtualRow.index}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                ...(effectiveWrapLines && { width: "100%" }),
                transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
              }}
            >
              <LogRow
                entry={entry}
                index={virtualRow.index}
                showTimestamp={settings.showTimestamp}
                showLabels={effectiveShowLabels}
                wrapLines={effectiveWrapLines}
                prettifyJSON={settings.prettifyJSON}
                labelsWidth={LABELS_WIDTH}
                commonLabels={commonLabels}
                onLabelClick={onLabelClick}
              />
            </div>
          );
        })}
      </div>

      {/* Loading overlay for additional loads */}
      {isLoading && logs.length > 0 && (
        <div className="logs-viewer-loading-overlay">
          <Spin size="small" />
        </div>
      )}
    </div>
  );
}
